import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useDeleteProcessById } from 'src/hooks/processes/delete-process.hook';
import { useGetProcessesWithPagination } from 'src/hooks/processes/get-processes-with-pagination.hook';

import PermissionCheck from 'src/components/permission-check';
import { DrawerComponent } from 'src/components/custom-drawer';
import { ProcessesForm } from './components/processes-form';
import ProcessesTable from './components/processes-table/processes-table.component';

import { DEFAULT_PROCESS, PROCESSES_MODALS } from './processes.constants';

import { ProcessesWrapper } from './processes.styles';
import { IProcess } from './processes.interfaces';

const Processes = ({
  employeeData,
  onClickEdit,
}: {
  employeeData: { memberId: string };
  onClickEdit: (process: IProcess) => void;
}) => {
  const { t } = useTranslation('employee');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [activeProcess, setActiveProcess] = useState(DEFAULT_PROCESS);

  const { processes, paginationConfig, loading, onTableChange } = useGetProcessesWithPagination();

  const handleCreateNewProcess = () => {
    setActiveModal(PROCESSES_MODALS.create);
  };

  const closeModal = () => {
    setActiveModal(null);
    setActiveProcess(DEFAULT_PROCESS);
  };

  const { deleteProcessById } = useDeleteProcessById({
    onSuccess: closeModal,
  });

  const deleteTemplate = () => {
    deleteProcessById({
      variables: {
        processId: activeProcess?._id,
      },
    });
    setActiveProcess(DEFAULT_PROCESS);
    closeModal();
  };

  const onSubmitDelete = () => {
    deleteTemplate();
  };

  return (
    <ProcessesWrapper>
      <DrawerComponent
        title={t(`process.labels.startNewProcess`)}
        onClose={closeModal}
        visible={activeModal === PROCESSES_MODALS.create}
        destroyOnClose
      >
        <ProcessesForm onCancel={closeModal} memberId={employeeData.memberId} />
      </DrawerComponent>
      <DeleteModal
        translate={t}
        isOpened={activeModal === PROCESSES_MODALS.delete}
        title={t('process.deleteModal.title')}
        subTitle={t('process.deleteModal.subtitle', {
          processName: activeProcess?.title,
        })}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <div>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('process.labels.processes')}
        </Typography>
        <PermissionCheck
          permission={PLATFORM_PERMISSIONS.employeeManagement}
          action={PERMISSION_ACTIONS.update}
        >
          <ButtonsWrapper>
            <Button
              type="primary"
              ghost
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={handleCreateNewProcess}
            >
              {t('process.buttons.startProcess')}
            </Button>
          </ButtonsWrapper>
        </PermissionCheck>
      </div>
      <ProcessesTable
        onClickEdit={onClickEdit}
        dataSource={processes}
        onTableChange={onTableChange}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setActiveProcess(record);
        }}
        loading={loading}
        paginationConfig={paginationConfig}
      />
    </ProcessesWrapper>
  );
};

export default Processes;
